/**
 * FindSimilarNodesModal — AI Actions: "Identify Semantically Similar Nodes".
 *
 * Calls the AI to find pairs of nodes that are semantically similar or
 * potentially duplicate. For each pair the user chooses which node (if any) to
 * remove, then applies the selected deletions in one step.
 */
import { useEffect, useState } from 'react';
import type { Node } from '@xyflow/react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tag,
  RadioButtonGroup,
  RadioButton,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { ontologyGenerationApi } from '@/lib/api/ontology-generation.api';
import type { SimilarNodePair } from '@shared/types/ontology';

type RemoveChoice = 'none' | 'a' | 'b';

interface FindSimilarNodesModalProps {
  isOpen: boolean;
  allNodes: Node[];
  onRemoveNodes: (nodeIds: string[]) => void;
  onClose: () => void;
}

const SIMILARITY_KIND: Record<SimilarNodePair['similarity'], 'red' | 'teal' | 'warm-gray'> = {
  high: 'red',
  medium: 'teal',
  low: 'warm-gray',
};

export function FindSimilarNodesModal({
  isOpen,
  allNodes,
  onRemoveNodes,
  onClose,
}: FindSimilarNodesModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pairs, setPairs] = useState<SimilarNodePair[]>([]);
  const [explanation, setExplanation] = useState('');
  // Per-pair choice: 'none' | 'a' | 'b' — default keep both
  const [choices, setChoices] = useState<Record<number, RemoveChoice>>({});

  const runFindSimilar = () => {
    setLoading(true);
    setError(null);
    setPairs([]);
    setExplanation('');
    setChoices({});

    ontologyGenerationApi
      .findSimilarNodes({
        nodes: allNodes.map((n) => ({
          id: n.id,
          label: (n.data?.label as string) || n.id,
          definition: n.data?.definition as string | undefined,
        })),
      })
      .then((response) => {
        setPairs(response.pairs);
        setExplanation(response.explanation);
        const initial: Record<number, RemoveChoice> = {};
        response.pairs.forEach((_, i) => { initial[i] = 'none'; });
        setChoices(initial);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError((err as Error)?.message || 'Failed to get AI suggestions');
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) runFindSimilar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const findLabel = (id: string): string => {
    const node = allNodes.find((n) => n.id === id);
    return (node?.data?.label as string) || id;
  };

  const handleApply = () => {
    const toRemove = new Set<string>();
    pairs.forEach((pair, i) => {
      const choice = choices[i];
      if (choice === 'a') toRemove.add(pair.nodeAId);
      if (choice === 'b') toRemove.add(pair.nodeBId);
    });
    if (toRemove.size > 0) onRemoveNodes([...toRemove]);
    onClose();
  };

  const removalsCount = Object.values(choices).filter((c) => c !== 'none').length;
  // Deduplicate: a node id may appear in multiple pairs
  const uniqueRemovals = new Set(
    pairs.flatMap((pair, i) => {
      const c = choices[i];
      if (c === 'a') return [pair.nodeAId];
      if (c === 'b') return [pair.nodeBId];
      return [];
    })
  ).size;

  return (
    <ComposedModal open={isOpen} onClose={onClose} size="lg">
      <ModalHeader title="Semantically Similar Nodes" label="AI-proposed deduplication" />
      <ModalBody>
        {loading && (
          <div style={{ marginBottom: '1rem' }}>
            <InlineLoading description="AI is analysing the graph…" status="active" />
          </div>
        )}

        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            hideCloseButton
            style={{ marginBottom: '1rem' }}
          />
        )}

        {!loading && !error && (
          <>
            {explanation && (
              <p style={{ marginBottom: '1rem', color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
                {explanation}
              </p>
            )}

            {pairs.length === 0 && (
              <InlineNotification
                kind="info"
                title="No similar nodes found"
                subtitle="The AI did not identify any semantically similar or duplicate node pairs."
                lowContrast
                hideCloseButton
              />
            )}

            {pairs.map((pair, index) => (
              <div
                key={index}
                style={{
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--cds-border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <strong>{findLabel(pair.nodeAId)}</strong>
                  <span style={{ color: 'var(--cds-text-secondary)' }}>↔</span>
                  <strong>{findLabel(pair.nodeBId)}</strong>
                  <Tag type={SIMILARITY_KIND[pair.similarity]} size="sm">
                    {pair.similarity}
                  </Tag>
                </div>
                {pair.rationale && (
                  <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', margin: '0.25rem 0 0.5rem' }}>
                    {pair.rationale}
                  </p>
                )}
                <RadioButtonGroup
                  name={`similar-pair-${index}`}
                  legendText="Remove"
                  valueSelected={choices[index] ?? 'none'}
                  onChange={(value: string | number | undefined) =>
                    setChoices((prev) => ({ ...prev, [index]: (value as RemoveChoice) ?? 'none' }))
                  }
                  orientation="horizontal"
                >
                  <RadioButton id={`pair-${index}-none`} labelText="Neither" value="none" />
                  <RadioButton id={`pair-${index}-a`} labelText={findLabel(pair.nodeAId)} value="a" />
                  <RadioButton id={`pair-${index}-b`} labelText={findLabel(pair.nodeBId)} value="b" />
                </RadioButtonGroup>
              </div>
            ))}
          </>
        )}

        {error && (
          <Button kind="tertiary" onClick={runFindSimilar} style={{ marginTop: '0.5rem' }}>
            Retry
          </Button>
        )}
      </ModalBody>
      <ModalFooter
        primaryButtonText={uniqueRemovals > 0 ? `Remove ${uniqueRemovals} node${uniqueRemovals > 1 ? 's' : ''}` : 'Apply'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleApply}
        onRequestClose={onClose}
        primaryButtonDisabled={loading || !!error || removalsCount === 0}
      >
        <></>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
